<template>
  <div class="h-auto w-full" @click="focusInput">
    <div
      :class="[
        ...errorClasses,
        ...focusClasses,
        ...disabledClasses,
        ...backgroundColors,
        ...textColors,
        ...sizeClasses,
        'rounded',
        size === 'x-small' ? '!h-auto' : ''
      ]"
      class="w-full flex items-center overflow-hidden box-border border border-n-100 dark:border-n-700 relative"
    >
      <slot name="left"></slot>
      <input
        v-bind="$attrs"
        ref="input"
        v-model="modelValue"
        class="appearance-none focus:outline-none w-full leading-tight bg-transparent font-body"
        :class="[
          ...placeholderClasses,
          ...sizeClasses,
          size === 'x-small' ? 'px-2 h-6' : 'px-4 h-full'
        ]"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :disabled="disabled"
        :maxlength="maxLength"
        :readonly="readOnly"
        :type="type"
      />
      <slot name="right"></slot>
      <div
        v-if="suffix && modelValue"
        class="absolute text-n-800 dark:text-n-200 pointer-events-none"
        :style="{
          left: `${inputTextXPosition + inputTextWidth + (size === 'x-small' ? 10 : 20)}px`,
          top: '50%',
          transform: 'translateY(-50%)'
        }"
      >
        {{ suffix }}
      </div>
    </div>
    <div v-if="errorMessage && wrongValue" class="text-sm text-r-500">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
  type InputHTMLAttributes
} from 'vue'

import {
  useBackgroundColorsClasses,
  useDisabledClasses,
  useErrorClasses,
  useFocusClasses,
  usePlaceholderClasses,
  useSizeClasses,
  useTextColorsClasses
} from '../classes'
import { type InputProps } from './input.types'

const props = withDefaults(defineProps<InputProps & /* @vue-ignore */ InputHTMLAttributes>(), {
  placeholder: '',
  errorMessage: '',
  size: 'small',
  maxLength: 255,
  autocomplete: 'off',
  wrongValue: false,
  readOnly: false,
  suffix: undefined
})

const inputRef = useTemplateRef('input')
const inputTextWidth = ref(0)
const inputTextXPosition = ref(0)

const errorClasses = computed(() => useErrorClasses(props.wrongValue))
const placeholderClasses = computed(() => usePlaceholderClasses(props.size))
const focusClasses = computed(() => useFocusClasses())
const disabledClasses = computed(() => useDisabledClasses(props.disabled))
const backgroundColors = computed(() => useBackgroundColorsClasses())
const textColors = computed(() => useTextColorsClasses())
const sizeClasses = computed(() => useSizeClasses(props.size))

const modelValue = defineModel<any>({ default: null })

function calculateInputTextWidth() {
  const input = inputRef.value
  if (!input || modelValue.value === null || modelValue.value === '') {
    inputTextWidth.value = 0
    inputTextXPosition.value = 0
    return
  }

  // Create a temporary span to measure the text width
  const span = document.createElement('span')
  span.style.visibility = 'hidden'
  span.style.position = 'absolute'
  span.style.whiteSpace = 'pre'

  // Copy the input's font styles to ensure accurate measurement
  const inputStyles = window.getComputedStyle(input)
  span.style.font = inputStyles.font
  span.style.fontSize = inputStyles.fontSize
  span.style.fontFamily = inputStyles.fontFamily
  span.style.fontWeight = inputStyles.fontWeight
  span.style.letterSpacing = inputStyles.letterSpacing

  span.textContent = input.value

  document.body.appendChild(span)
  inputTextWidth.value = span.getBoundingClientRect().width
  document.body.removeChild(span)

  const inputRect = input.getBoundingClientRect()
  const parentRect = input.parentElement?.getBoundingClientRect() || { left: 0 }

  inputTextXPosition.value = inputRect.left - parentRect.left
}

watch(
  () => modelValue.value,
  async () => {
    if (props.suffix) {
      await nextTick()
      calculateInputTextWidth()
    }
  },
  { immediate: true }
)

watch(
  () => props.suffix,
  () => {
    if (!props.suffix) return

    window.addEventListener('resize', calculateInputTextWidth)
  },
  { immediate: true }
)

onUnmounted(() => {
  if (props.suffix) {
    window.removeEventListener('resize', calculateInputTextWidth)
  }
})

function focusInput() {
  inputRef.value?.focus()
}

defineExpose({ focusInput, inputRef })
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
